import React from "react";
import { useTranslation } from "react-i18next";

import UserHelpApi, { type UserHelpFile } from "api/UserHelpApi";
import { normalizeLanguage } from "config/i18n";
import { getFileExtension, getPreviewKind } from "helper/file";
import { getApiData } from "../../types/api";
import DocumentLibraryPreviewPopin from "../DocumentLibrary/DocumentLibraryPreviewPopin";
import DocumentLibraryViewer from "../DocumentLibrary/DocumentLibraryViewer";

interface LoadedUserHelp {
  file: UserHelpFile;
  data: Uint8Array;
  url: string;
}

interface UserHelpState {
  language: string;
  loaded: LoadedUserHelp | null;
  isLoading: boolean;
}

export default function UserHelpDocument() {
  const { t, i18n } = useTranslation();
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language) || "fr";
  const [state, setState] = React.useState<UserHelpState>({
    language,
    loaded: null,
    isLoading: true,
  });

  React.useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;

    UserHelpApi.getFile(language)
      .then((response) => {
        const file = getApiData(response, {}) as UserHelpFile;
        const data = Uint8Array.from(atob(file.filedata || ""), (character) =>
          character.charCodeAt(0)
        );
        objectUrl = window.URL.createObjectURL(
          new Blob([data], { type: file.mime || "application/octet-stream" })
        );

        if (active) {
          setState({ language, loaded: { file, data, url: objectUrl }, isLoading: false });
        } else {
          window.URL.revokeObjectURL(objectUrl);
          objectUrl = null;
        }
      })
      .catch(() => {
        if (active) {
          setState({ language, loaded: null, isLoading: false });
        }
      });

    return () => {
      active = false;
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [language]);

  const loaded = state.language === language ? state.loaded : null;
  const isLoading = state.language !== language || state.isLoading;
  const file = loaded?.file;
  const extension = file?.extension || getFileExtension(file?.filename || "");
  const kind = getPreviewKind({
    mime: file?.mime,
    originalExtension: extension,
  });
  const viewer = (
    <DocumentLibraryViewer
      t={t}
      isLoading={isLoading}
      documentFile={loaded ? { preview: file?.preview } : null}
      previewSource={loaded?.data || null}
      previewUrl={loaded?.url || null}
      kind={kind}
      fileName={file?.filename}
    />
  );

  return (
    <DocumentLibraryPreviewPopin
      title={file?.title || t("footer.links.help")}
      kindLabel={extension ? extension.toUpperCase() : undefined}
      fileName={file?.filename}
      viewer={viewer}
    />
  );
}
